// Number/money/date formatting helpers shared across the SABER read screens.
// Money/kWh fields from the API are plain JSON numbers (see saber.types.ts).

/** $1,234.56 — currency with 2 decimals. Renders an em dash for nullish. */
export const money = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—'
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** 1,234 kWh — integer kWh with a unit suffix. */
export const kwh = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—'
  return `${Math.round(value).toLocaleString()} kWh`
}

/** $1,235 — currency rounded to whole dollars. Renders an em dash for nullish. */
export const money0 = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—'
  return `$${Math.round(value).toLocaleString()}`
}

/** 1,234 — integer with thousands separators, no unit. */
export const integer = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—'
  return Math.round(value).toLocaleString()
}

/** 97.5% — percentage already expressed as a 0–100 number. Rounds to `digits` decimals; -0 normalizes to 0.0%. */
export const percent = (value: number | null | undefined, digits = 1): string => {
  if (value == null || Number.isNaN(value)) return '—'
  const scale = 10 ** digits
  const rounded = Math.round(value * scale) / scale
  const normalized = rounded === 0 ? 0 : rounded
  return `${normalized.toFixed(digits)}%`
}

/** $0.22 — rate with 2 decimals (§12). Use rate4 for per-kWh precision. */
export const rate = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—'
  return `$${value.toFixed(2)}`
}

/** $0.2210 — per-kWh rate with 4 decimals. */
export const rate4 = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—'
  return `$${value.toFixed(4)}`
}

/** Parse YYYY-MM-DD at local midnight — bare ISO dates are UTC in JS and shift a day in US tz. */
const parseDateString = (value: string): Date =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value)

/** Format an ISO date/datetime string as a short local date+time. Passes through nullish as an em dash. */
export const dateTime = (value: string | null | undefined): string => {
  if (value == null || value === '') return '—'
  const d = parseDateString(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/** Format a YYYY-MM-DD (or ISO) string as a short local date. */
export const date = (value: string | null | undefined): string => {
  if (value == null || value === '') return '—'
  const d = parseDateString(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/** Seconds → "12m 30s" / "45s". */
export const duration = (seconds: number | null | undefined): string => {
  if (seconds == null || Number.isNaN(seconds)) return '—'
  const total = Math.round(seconds)
  const mins = Math.floor(total / 60)
  const secs = total % 60
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
}

/** Render a +/- signed dollar delta, e.g. +$124.50 / -$18.20 / $0.00 at zero. */
export const signedMoney = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—'
  const absFormatted = Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
  if (Math.abs(value) < 0.005) return `$${absFormatted}`
  const sign = value < 0 ? '-' : '+'
  return `${sign}$${absFormatted}`
}

/** Render a +/- signed integer kWh delta, e.g. +100 / -100. */
export const signedInteger = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—'
  const sign = value < 0 ? '-' : '+'
  return `${sign}${Math.abs(Math.round(value)).toLocaleString()}`
}
